import { SelectedEstimateOption } from '@/types/estimates/SelectedEstimateOptionType';
import { roundUpPrice } from '@/utils/circuits/prices/formatPrice';
import {
  computeInternationalFlightsMarginAmount,
  InternationalFlightsPricingInput,
} from '@/utils/estimates/internationalFlightsPricing';

export type AgencyMarginBreakdownInput = {
  allowAgencyMarginRate: boolean;
  allowAgencyAdditionalMargin: boolean;
  allowAgencyInternationalFlights: boolean;
  agencyMarginRate: number;
  additionalMarginType?: 'FIXED' | 'PERCENT' | null;
  additionalMarginValue?: number | string | null;
  /** Circuit total with general agency margin (excl. options, excl. extra margin). */
  circuitWithGeneralMargin: number;
  selectedOptions: Record<string, SelectedEstimateOption>;
  distributorMarginPriceFactor?: number;
  flightsPricing?: InternationalFlightsPricingInput;
};

export type AgencyMarginBreakdown = {
  generalMargin: number | null;
  additionalMargin: number | null;
  flightsMargin: number | null;
  totalMargin: number | null;
};

const toNumeric = (value: number | string | null | undefined): number | null => {
  if (value == null || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const sumSelectedOptionsMarged = (
  selectedOptions: Record<string, SelectedEstimateOption>,
  distributorMarginPriceFactor: number,
): number =>
  Object.values(selectedOptions).reduce(
    (acc, { deltaPrice, carCategoryOptions }) =>
      acc +
      roundUpPrice((deltaPrice ?? 0) * distributorMarginPriceFactor) +
      Object.values(carCategoryOptions ?? {})
        .map((categoryElement) =>
          categoryElement
            .filter(({ isRequired }) => !isRequired)
            .reduce(
              (innerAcc, { deltaPrice: optionDelta }) =>
                innerAcc +
                roundUpPrice((optionDelta ?? 0) * distributorMarginPriceFactor),
              0,
            ),
        )
        .reduce((innerAcc, val) => innerAcc + val, 0),
    0,
  );

/** Computes agency margin amounts shown in the « Vos marges » block. */
export const computeAgencyMarginBreakdown = ({
  allowAgencyMarginRate,
  allowAgencyAdditionalMargin,
  allowAgencyInternationalFlights,
  agencyMarginRate,
  additionalMarginType = 'FIXED',
  additionalMarginValue,
  circuitWithGeneralMargin,
  selectedOptions,
  distributorMarginPriceFactor = 1,
  flightsPricing,
}: AgencyMarginBreakdownInput): AgencyMarginBreakdown => {
  const optionsMargedTotal = sumSelectedOptionsMarged(
    selectedOptions,
    distributorMarginPriceFactor,
  );
  const circuitAndOptionsMarged = roundUpPrice(
    circuitWithGeneralMargin + optionsMargedTotal,
  );

  const marginRate = allowAgencyMarginRate ? Math.max(0, agencyMarginRate) : 0;
  const circuitAndOptionsBase =
    marginRate > 0
      ? roundUpPrice(circuitAndOptionsMarged / (1 + marginRate / 100))
      : circuitAndOptionsMarged;

  const generalMargin =
    marginRate > 0
      ? roundUpPrice(circuitAndOptionsBase * (marginRate / 100))
      : null;

  let additionalMargin: number | null = null;
  if (allowAgencyAdditionalMargin) {
    const additionalValue = toNumeric(additionalMarginValue);
    if (additionalValue != null && additionalValue > 0) {
      additionalMargin =
        additionalMarginType === 'PERCENT'
          ? roundUpPrice(circuitAndOptionsMarged * (additionalValue / 100))
          : roundUpPrice(additionalValue);
    }
  }

  const flightsMargin =
    allowAgencyInternationalFlights && flightsPricing
      ? computeInternationalFlightsMarginAmount(flightsPricing) || null
      : null;

  const parts = [generalMargin, additionalMargin, flightsMargin].filter(
    (value): value is number => value != null && value > 0,
  );

  if (parts.length === 0) {
    return {
      generalMargin: null,
      additionalMargin: null,
      flightsMargin: null,
      totalMargin: null,
    };
  }

  return {
    generalMargin,
    additionalMargin,
    flightsMargin,
    totalMargin: roundUpPrice(parts.reduce((sum, value) => sum + value, 0)),
  };
};
